import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Loader2, Users, Plus, Search, Lock, Globe, ChevronRight, Filter
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { NavBar } from '../components/NavBar';

const API = process.env.REACT_APP_BACKEND_URL;

export const GroupsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('browse');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [joiningGroup, setJoiningGroup] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedCategory, search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupsRes, myGroupsRes, categoriesRes] = await Promise.all([
        api().get(`/groups/?${selectedCategory !== 'all' ? `category=${selectedCategory}&` : ''}${search ? `search=${search}` : ''}`),
        api().get('/groups/?my_groups=true'),
        api().get('/groups/categories')
      ]);
      
      setGroups(groupsRes.data.groups || []);
      setMyGroups(myGroupsRes.data.groups || []);
      setCategories(categoriesRes.data.categories || {});
    } catch (err) {
      toast.error('Failed to load groups');
    }
    setLoading(false);
  };

  const handleJoinGroup = async (groupId) => {
    setJoiningGroup(groupId);
    try {
      await api().post(`/groups/${groupId}/join`);
      toast.success('Joined group!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join group');
    }
    setJoiningGroup(null);
  };

  const handleLeaveGroup = async (groupId) => {
    if (!window.confirm('Leave this group?')) return;
    try {
      await api().delete(`/groups/${groupId}/leave`);
      toast.success('Left group');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to leave group');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  return (
    <div className="min-h-screen bg-black" data-testid="groups-page">
      <NavBar title="Fitness Groups" />

      <main className="container mx-auto px-6 py-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Users className="h-6 w-6 text-primary" />
            <span className="text-primary font-heading text-xl tracking-widest uppercase">Community</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider mb-4">
            Fitness <span className="text-primary">Groups</span>
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            Join groups with like-minded fitness enthusiasts. Share tips, stay motivated, and grow together!
          </p>
        </motion.div>

        {/* Create Button & Tabs */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
          <div className="flex gap-2">
            <Button
              onClick={() => setActiveTab('browse')}
              variant={activeTab === 'browse' ? 'default' : 'outline'}
              className={activeTab === 'browse' ? 'bg-primary text-black' : ''}
            >
              Browse Groups
            </Button>
            <Button
              onClick={() => setActiveTab('my')}
              variant={activeTab === 'my' ? 'default' : 'outline'}
              className={activeTab === 'my' ? 'bg-primary text-black' : ''}
            >
              My Groups ({myGroups.length})
            </Button>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="bg-primary text-black">
            <Plus className="h-4 w-4 mr-2" />
            Create Group
          </Button>
        </div>

        {activeTab === 'browse' && (
          <>
            {/* Search */}
            <form onSubmit={handleSearch} className="max-w-md mx-auto mb-8">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search groups..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 bg-card border-border"
                  />
                </div>
                <Button type="submit" className="bg-primary text-black">Search</Button>
              </div>
            </form>

            {/* Category Filter — dropdown for a cleaner, less crowded layout
                (especially on mobile where 11 pills wrap to 4+ rows). */}
            <div className="max-w-md mx-auto mb-8" data-testid="group-category-filter-wrap">
              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}
              >
                <SelectTrigger
                  className="bg-card border-border text-white"
                  data-testid="group-category-select-trigger"
                >
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-primary" />
                    <SelectValue placeholder="All categories" />
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all" data-testid="group-category-option-all">
                    🌐 All Categories
                  </SelectItem>
                  {Object.entries(categories).map(([key, cat]) => (
                    <SelectItem
                      key={key}
                      value={key}
                      data-testid={`group-category-option-${key}`}
                    >
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {/* Groups Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-12 w-12 text-primary animate-spin" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(activeTab === 'browse' ? groups : myGroups).map((group, i) => (
              <motion.div
                key={group.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="bg-card border border-border overflow-hidden hover:border-primary/50 transition-colors"
              >
                {/* Cover Image or Gradient */}
                <div className="h-32 bg-gradient-to-br from-primary/30 to-purple-500/30 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-6xl">
                      {categories[group.category]?.icon || '💪'}
                    </span>
                  </div>
                  {group.is_private && (
                    <Badge className="absolute top-3 right-3 bg-black/50">
                      <Lock className="h-3 w-3 mr-1" />
                      Private
                    </Badge>
                  )}
                </div>

                <div className="p-6">
                  <Link to={`/groups/${group.id}`}>
                    <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-2 hover:text-primary transition-colors">
                      {group.name}
                    </h3>
                  </Link>
                  
                  <Badge className="mb-3 bg-primary/10 text-primary">
                    {categories[group.category]?.name || group.category}
                  </Badge>
                  
                  <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                    {group.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
                    </span>
                    
                    {group.is_member ? (
                      <div className="flex gap-2">
                        <Link to={`/groups/${group.id}`}>
                          <Button size="sm" variant="outline">
                            View
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleJoinGroup(group.id)}
                        disabled={joiningGroup === group.id}
                        className="bg-primary text-black"
                      >
                        {joiningGroup === group.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Join'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {!loading && (activeTab === 'browse' ? groups : myGroups).length === 0 && (
          <div className="text-center py-16 bg-card border border-border max-w-md mx-auto">
            <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading text-xl text-white mb-2">
              {activeTab === 'browse' ? 'No Groups Found' : 'No Groups Yet'}
            </h3>
            <p className="text-muted-foreground mb-4">
              {activeTab === 'browse' 
                ? 'Try a different search or category' 
                : 'Join a group or create your own!'}
            </p>
            {activeTab === 'my' && (
              <Button onClick={() => setActiveTab('browse')} className="bg-primary text-black">
                Browse Groups
              </Button>
            )}
          </div>
        )}
      </main>

      {/* Create Group Modal */}
      <CreateGroupModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        categories={categories}
        api={api}
        onCreated={(group) => {
          setShowCreateModal(false);
          navigate(`/groups/${group.id}`);
        }}
      />
    </div>
  );
};

// Create Group Modal Component
const CreateGroupModal = ({ isOpen, onClose, categories, api, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general_fitness');
  const [isPrivate, setIsPrivate] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setCreating(true);
    try {
      const res = await api().post('/groups/', {
        name: name.trim(),
        description: description.trim(),
        category,
        is_private: isPrivate
      });
      toast.success('Group created!');
      onCreated(res.data.group);
      // Reset form
      setName('');
      setDescription('');
      setCategory('general_fitness');
      setIsPrivate(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create group');
    }
    setCreating(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative bg-card border border-border p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <h2 className="font-heading text-2xl text-white uppercase tracking-wider mb-6">
          Create Group
        </h2>

        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-white">Group Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Morning Runners"
              className="mt-1 bg-black border-border"
              maxLength={50}
            />
          </div>

          <div>
            <Label htmlFor="description" className="text-white">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this group about?"
              className="mt-1 bg-black border-border"
              rows={3}
              maxLength={500}
            />
          </div>

          <div>
            <Label className="text-white">Category</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {Object.entries(categories).map(([key, cat]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCategory(key)}
                  className={`p-2 text-center text-xs border transition-colors ${
                    category === key 
                      ? 'bg-primary text-black border-primary' 
                      : 'bg-black border-border text-white hover:border-primary/50'
                  }`}
                >
                  <span className="text-lg block mb-1">{cat.icon}</span>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="private"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              className="accent-primary"
            />
            <Label htmlFor="private" className="text-white flex items-center gap-2 cursor-pointer">
              <Lock className="h-4 w-4" />
              Private Group (Invite only)
            </Label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={creating} className="flex-1 bg-primary text-black">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Group'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default GroupsPage;
